import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/Card';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import Loading from '../components/ui/Loading';

const ModuleSelectionPage = () => {
  const navigate = useNavigate();
  const { user, logout, hasModuleAccess } = useAuth();
  const [isTransitioning, setIsTransitioning] = React.useState(true);

  React.useEffect(() => {
    const timer = setTimeout(() => setIsTransitioning(false), 800);
    return () => clearTimeout(timer);
  }, []);

  // Departman bazlı modül izinleri
  const getAvailableModules = () => {
    // Backend'den gelen response'da department bir number, object değil
    const userDepartmentId =
      (typeof user?.department === 'object' ? user?.department?.id : user?.department) ||
      user?.department_id;
    const userRoleName = String(user?.role_name || user?.role || '').trim();
    const userDepartmentName = String(user?.department?.department_name || user?.department_name || '').trim();

    // Tüm modüller
    const allModules = [
      {
        id: 'helpdesk',
        name: 'Help Desk',
        description: 'Otel IT destek hizmetleri ve ticket yönetimi',
        icon: '🎫',
        color: 'bg-blue-500',
        route: '/helpdesk/dashboard',
        allowedDepartments: 'all' // Tüm departmanlar erişebilir
      },
      {
        id: 'mail-summary',
        name: 'Mail Özeti (M365)',
        description: 'Okunmamış e-postaların AI ile Türkçe özeti (Microsoft 365)',
        icon: '📧',
        color: 'bg-sky-600',
        route: '/mail/summary',
        allowedDepartments: 'all'
      },
      {
        id: 'stopsale',
        name: 'Sales Marketing',
        description: 'Satış durdurma ve oda yönetimi sistemi',
        icon: '🏨',
        color: 'bg-red-500',
        route: '/stopsale/dashboard',
        allowedDepartments: [10, 20, 30, 40, 50, 60, 91, 101, 111] // Sadece belirli departmanlar
      },
      {
        id: 'notes',
        name: 'Notes',
        description: 'Ortak bilgi bankası ve dokümantasyon',
        icon: '📝',
        color: 'bg-green-500',
        route: '/notes',
        allowedDepartments: 'all' // Tüm departmanlar erişebilir
      },
      {
        id: 'quality-management',
        name: 'Quality Management',
        description: 'Corrective action and quality process management',
        icon: '✅',
        color: 'bg-purple-500',
        route: '/quality-management/df-baslat',
        allowedDepartments: 'all'
      },
      {
        id: 'food-beverage',
        name: 'Food and Beverage',
        description: 'QR menü yönetimi ve dijital restoran içerikleri',
        icon: '🍽️',
        color: 'bg-amber-500',
        route: '/food-beverage/hotels',
        allowedDepartments: [
          130, 131, 132, 133, 134, 135, 136, 137, 138,
          1, 11, 21, 31, 41, 51, 82, 92, 102,
          2, 12, 22, 32, 42, 52, 83, 93, 103
        ]
      },
      {
        id: 'general',
        name: 'General',
        description: 'Lisans yönetimi ve genel işlemler',
        icon: '🔑',
        color: 'bg-indigo-500',
        route: '/general/licenses',
        allowedDepartments: 'all'
      },
      {
        id: 'internal-audit',
        name: 'Denetim Modülü',
        description: 'Word→Excel dönüşüm ve ses transkripsiyon araçları',
        icon: '🛡️',
        color: 'bg-teal-600',
        route: '/internal-audit/doc-convert',
        allowedDepartments: 'all',
        isVisible: hasModuleAccess('internal_audit')
      },
      {
        id: 'vehicle-maintenance',
        name: 'Araç Bakım',
        description: 'Araç takip, bakım, muayene ve sigorta yönetimi',
        icon: '🚗',
        color: 'bg-emerald-600',
        route: '/vehicle-maintenance/vehicles',
        allowedDepartments: 'all',
        isVisible: hasModuleAccess('vehicle_maintenance')
      },
      {
        id: 'camasirhane',
        name: 'Çamaşırhane',
        description: 'Otel tekstil tartım, üretim, maliyet ve raporlama',
        icon: '🧺',
        color: 'bg-yellow-600',
        route: '/camasirhane/weighing',
        allowedDepartments: 'all',
        isVisible: hasModuleAccess('camasirhane')
      }
    ];

    // Kullanıcının departmanına ve rolüne göre modülleri filtrele
    return allModules.filter(module => {
      let roleAllowed = true;
      let departmentAllowed = true;

      if (module.allowedRoles && Array.isArray(module.allowedRoles)) {
        roleAllowed = module.allowedRoles.includes(userRoleName);
      }

      if (module.allowedDepartmentNames && Array.isArray(module.allowedDepartmentNames)) {
        departmentAllowed = module.allowedDepartmentNames.some(
          (name) => name.toLowerCase() === userDepartmentName.toLowerCase()
        );
      } else if (module.allowedDepartments === 'all') {
        departmentAllowed = true;
      } else if (Array.isArray(module.allowedDepartments)) {
        departmentAllowed = module.allowedDepartments.includes(userDepartmentId);
      }

      return roleAllowed && departmentAllowed && module.isVisible !== false;
    });
  };

  // Kullanıcının erişebileceği modüller
  const modules = getAvailableModules();

  const handleModuleSelect = (module) => {
    setIsTransitioning(true);
    setTimeout(() => {
      navigate(module.route);
    }, 500);
  };

  const handleLogout = async () => {
    setIsTransitioning(true);
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-slate-800 dark:to-gray-900 relative overflow-hidden">
      {isTransitioning && (
        <div className="fixed inset-0 z-[100] bg-gray-50/80 dark:bg-gray-900/80 backdrop-blur-sm flex items-center justify-center">
          <Loading fullScreen={false} message="Hazırlanıyor..." />
        </div>
      )}
      <div className={`transition-all duration-500 ${isTransitioning ? 'opacity-0 invisible scale-95' : 'opacity-100 visible scale-100'}`}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,_rgba(0,0,0,0.1)_1px,_transparent_0)] dark:bg-[radial-gradient(circle_at_1px_1px,_rgba(255,255,255,0.1)_1px,_transparent_0)] bg-[length:32px_32px]"></div>
        </div>

        {/* Header */}
        <div className="relative z-10 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-lg border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-white font-bold text-sm">M</span>
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Modül Seçimi
                  </h1>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Workspace Manager</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <p className="text-xs text-gray-600 dark:text-gray-400">Hoş geldin</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{user?.full_name || user?.email}</p>
                  {user?.department && (
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      Departman: {user.department.department_name} (ID: {user.department.id})
                    </p>
                  )}
                </div>
                <button
                  onClick={handleLogout}
                  className="px-3 py-1.5 text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-md transition-all duration-200 font-medium"
                >
                  Çıkış Yap
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
              Hangi Modülü Kullanmak İstiyorsunuz?
            </h2>
            <p className="text-base text-gray-700 dark:text-gray-300 max-w-lg mx-auto">
              Çalışmak istediğiniz modülü seçerek işlemlerinize başlayın
            </p>
          </div>

          {/* Module Cards */}
          <div className={`grid gap-4 max-w-3xl mx-auto ${modules.length === 1 ? 'grid-cols-1 max-w-md' : 'lg:grid-cols-2'}`}>
            {(modules || []).filter(module => module && module.id).map((module, index) => (
              <Card
                key={module.id}
                className="group relative p-4 hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1 border border-gray-300 dark:border-gray-700 bg-white/80 dark:bg-gray-800/60 backdrop-blur-sm hover:bg-white/90 dark:hover:bg-gray-800/80 rounded-md"
                onClick={() => handleModuleSelect(module)}
              >
                <div className="flex items-start space-x-3">
                  {/* Module Icon */}
                  <div className={`flex-shrink-0 w-10 h-10 rounded-md ${module.color} text-white text-lg flex items-center justify-center shadow-md`}>
                    {module.icon}
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Module Name */}
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      {module.name}
                    </h3>

                    {/* Action Button */}
                    <button className={`inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium text-white transition-all duration-200 ${module.color} hover:shadow-md transform hover:scale-105`}>
                      <span className="mr-1">Modüle Git</span>
                      <ArrowRightIcon className="w-3 h-3 transform group-hover:translate-x-1 transition-transform duration-300" />
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Footer Info */}
          {modules.length === 0 && (
            <div className="text-center mt-8">
              <div className="inline-flex items-center px-4 py-2 bg-yellow-100 dark:bg-yellow-900/20 backdrop-blur-sm rounded-lg shadow-md border border-yellow-300 dark:border-yellow-700">
                <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full mr-2"></div>
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  Departmanınız için erişilebilir modül bulunmuyor. Lütfen yöneticinizle iletişime geçin.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Bottom Decoration */}
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-gray-200/20 dark:from-black/20 to-transparent pointer-events-none"></div>
      </div>
    </div>
  );
};

export default ModuleSelectionPage;
